import { PlusIcon } from '@/components/icons/plus-icon';
import { Image } from '@/components/ui/image';
import { useModalAction } from '@/components/ui/modal/modal.context';
import { Routes } from '@/config/routes';
import { cn } from '@/lib/cn';
import { productPlaceholder } from '@/lib/placeholders';
import usePrice from '@/lib/use-price';
import { Product } from '@/types';
import { useTranslation } from 'next-i18next';
import dynamic from 'next/dynamic';
import Link from 'next/link';
const AddToCart = dynamic(
  () =>
    import('@/components/products/add-to-cart/add-to-cart').then(
      (module) => module.AddToCart,
    ),
  { ssr: false },
);

const Oganesson: React.FC<{ product: Product; className?: string }> = ({
  product,
  className,
}) => {
  const { t } = useTranslation('common');
  const { name, image, quantity } = product ?? {};
  const { price, basePrice, discount } = usePrice({
    amount: product.sale_price ? product.sale_price : product.price!,
    baseAmount: product.price,
  });

  const { openModal } = useModalAction();

  function handleProductQuickView() {
    return openModal('PRODUCT_DETAILS', product.slug);
  }

  return (
    <article
      className={cn(
        'product-card cart-type-argon h-full transform overflow-hidden rounded bg-light shadow-downfall-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-downfall-lg',
        className,
      )}
    >
      <div className="relative flex h-48 w-auto items-center justify-center overflow-hidden bg-light transition-shadow sm:h-64">
        <span className="sr-only">{t('text-product-image')}</span>
        <Link href={Routes.products + '/' + product.slug} target="_blank">
          <Image
            src={image?.original ?? productPlaceholder}
            alt={name}
            fill
            sizes="(max-width: 768px) 100vw"
            className="product-image object-contain"
            role="button"
          />
        </Link>
        {discount && (
          <div className="absolute top-3 rounded bg-accent px-1.5 text-xs font-semibold leading-6 text-light md:top-[22px] md:px-2 lg:px-2.5 ltr:right-3 ltr:md:right-4 rtl:left-3 rtl:md:left-4">
            {discount}
          </div>
        )}
        <div className="absolute bottom-4 ltr:right-4 rtl:left-4">
          {Number(quantity) > 0 &&
            (product.product_type === 'simple' ? (
              <AddToCart variant="oganesson" data={product} />
            ) : (
              <button
                onClick={handleProductQuickView}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-accent text-sm text-light shadow-500 transition-colors hover:border-accent hover:bg-accent-hover hover:text-light focus:border-accent focus:bg-accent focus:text-light focus:outline-0 md:h-10 md:w-10"
              >
                <span className="sr-only">{t('text-plus')}</span>
                <PlusIcon className="h-5 w-5 stroke-2 md:h-6 md:w-6" />
              </button>
            ))}

          {Number(quantity) <= 0 && (
            <div className="rounded bg-red-500 px-2 py-1 text-xs text-light">
              {t('text-out-stock')}
            </div>
          )}
        </div>
      </div>

      <header className="p-3 py-3">
        <div className="mb-2 flex items-center">
          <span className="text-sm font-semibold text-heading md:text-base">
            {price}
          </span>
          {basePrice && (
            <del className="text-xs text-muted md:text-sm ltr:ml-2 rtl:mr-2">
              {basePrice}
            </del>
          )}
        </div>

        <h3 className="truncate text-xs text-body md:text-sm">{name}</h3>
      </header>
    </article>
  );
};

export default Oganesson;
